import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { EASE } from "./Reveal";
import { scrollToId } from "@/lib/smoothScroll";

const LINES = [
  <>Rooted in tradition.</>,
  <>Reimagined</>,
  <>for <span className="italic text-[#E2BE5A]">royalty.</span></>,
];

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }}
      transition={{ duration: 1.3, ease: EASE, delay: 0.35 + i * 0.14 }}>
      {children}
    </motion.span>
  </span>
);

const TiltFrame = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 90, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 90, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="[perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.6, ease: EASE, delay: 0.5 }}
        className="relative mx-auto w-[78%] max-w-[440px] sm:w-[62%] lg:w-full">
        <div className="absolute -inset-3 rounded-t-full border border-[#E2BE5A]/40" style={{ transform: "translateZ(-40px)" }} />
        <div className="relative aspect-[3/4] overflow-hidden rounded-t-full border border-[#E2BE5A]/60 shadow-[0_40px_90px_rgba(0,0,0,0.7)]">
          <img src="/images/real/wear_wedding.jpg" alt="Model wearing the Black Mamba kaba over an emerald dress at a garden wedding"
            className="h-full w-full object-cover object-[center_20%]" data-testid="hero-image" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,transparent_55%,rgba(11,11,12,0.18)_100%)]" />
        </div>
        <span className="absolute -bottom-5 -left-4 rounded-full border border-gold/40 bg-ink/80 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gold backdrop-blur-md sm:-left-8"
          style={{ transform: "translateZ(60px)" }}>
          The new modern kabas
        </span>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const wordX = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-[#3B1466] pt-28 lg:pt-24">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_72%_35%,rgba(120,60,180,0.45),transparent_60%)]" />
      <motion.span aria-hidden="true" style={{ x: wordX }}
        className="hero-outline hero-outline-gold pointer-events-none absolute bottom-[-4vw] left-0 select-none whitespace-nowrap font-display text-[34vw] italic leading-none lg:text-[24vw]">
        Kaba · Kaba
      </motion.span>

      <div className="relative z-10 mx-auto grid max-w-[1400px] items-center gap-14 px-5 pb-20 sm:px-10 lg:min-h-[calc(100svh-6rem)] lg:grid-cols-[1.15fr_0.85fr] lg:gap-10">
        <div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }}
            className="mb-8 font-mono text-[11px] uppercase tracking-[0.3em] text-[#E2BE5A] sm:text-xs" data-testid="hero-eyebrow">
            New · The modern kaba, 2026
          </motion.p>
          <h1 data-testid="hero-headline" className="font-display text-5xl font-light leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-[5.4rem]">
            {LINES.map((l, i) => <MaskLine key={i} i={i}>{l}</MaskLine>)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 1 }}
            className="mt-8 max-w-md text-base font-light leading-relaxed text-white/85 sm:text-lg">
            Once reserved for clergy and royalty — the velvet and gold kaba, reimagined for today’s woman.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 1.15 }}
            className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={() => scrollToId("collection")} data-testid="hero-cta-collection"
              className="group flex items-center gap-3 rounded-full bg-[#E2BE5A] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#0B0B0C] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-white">
              See the new kabas
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </button>
            <button onClick={() => scrollToId("order")} data-testid="hero-cta-order"
              className="rounded-full border border-[#E2BE5A]/70 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#E2BE5A] transition-[border-color,color] duration-300 hover:border-white hover:text-white">
              Order yours
            </button>
          </motion.div>
        </div>
        <motion.div style={{ y: imgY }}>
          <TiltFrame />
        </motion.div>
      </div>
    </section>
  );
};
